import React, { useEffect, useState } from 'react';
import { get2FAStatus, setup2FA, enable2FA, disable2FA } from '../../api/auth';

type Stage = 'idle' | 'setup' | 'enabled' | 'disable';

export function TwoFactorPanel() {
  const [stage, setStage] = useState<Stage>('idle');
  const [enabled, setEnabled] = useState(false);
  const [enabledAt, setEnabledAt] = useState<string | null>(null);
  const [backupCount, setBackupCount] = useState(0);
  const [secret, setSecret] = useState<string>('');
  const [otpauthUrl, setOtpauthUrl] = useState<string>('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  async function refresh() {
    const s = await get2FAStatus();
    setEnabled(!!s.totp_enabled);
    setEnabledAt(s.totp_enabled_at);
    setBackupCount(Number(s.backup_codes_remaining ?? 0));
    setStage(s.totp_enabled ? 'enabled' : 'idle');
  }

  useEffect(() => { void refresh(); }, []);

  async function startSetup() {
    setError(null); setBusy(true);
    try {
      const r = await setup2FA();
      setSecret(r.secret); setOtpauthUrl(r.otpauth_url);
      setStage('setup');
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Setup fehlgeschlagen.');
    } finally { setBusy(false); }
  }

  async function confirmEnable() {
    setError(null); setBusy(true);
    try {
      const r = await enable2FA(code);
      setBackupCodes(r.backup_codes);
      setCode('');
      await refresh();
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Code falsch.');
    } finally { setBusy(false); }
  }

  async function confirmDisable() {
    setError(null); setBusy(true);
    try {
      await disable2FA(password, code);
      setPassword(''); setCode(''); setBackupCodes([]);
      await refresh();
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Passwort oder Code falsch.');
    } finally { setBusy(false); }
  }

  if (stage === 'idle') {
    return (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginTop: 0, color: 'var(--text2)' }}>
          Schütze deinen Account zusätzlich mit einer Authenticator-App (Google Authenticator, 1Password, Authy).
        </p>
        <button onClick={startSetup} disabled={busy} style={btnPrimary}>2FA aktivieren</button>
        {error && <div style={errorBox}>{error}</div>}
      </div>
    );
  }

  if (stage === 'setup') {
    return (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginTop: 0 }}>
          1. Scan diesen QR-Code mit deiner Authenticator-App, oder gib den Geheim-Code manuell ein.
        </p>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <img
            alt="2FA QR-Code"
            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(otpauthUrl)}`}
            style={{ borderRadius: 8, background: '#fff', padding: 4 }}
          />
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Geheim-Code</div>
            <code style={{ fontSize: 14, padding: '6px 10px', background: 'var(--surface2)', borderRadius: 4, display: 'inline-block', marginTop: 4 }}>
              {secret}
            </code>
          </div>
        </div>
        <p style={{ marginBottom: 6 }}>2. Gib den 6-stelligen Code aus der App ein:</p>
        <input
          type="text" inputMode="numeric" maxLength={6} value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="123456"
          style={{ padding: '8px 12px', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 16, letterSpacing: 4, textAlign: 'center', width: 140 }}
        />
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button onClick={confirmEnable} disabled={busy || code.length !== 6} style={btnPrimary}>Aktivieren</button>
          <button onClick={() => setStage('idle')} disabled={busy} style={btnSecondary}>Abbrechen</button>
        </div>
        {backupCodes.length > 0 && (
          <div style={{ marginTop: 16, padding: 12, background: 'var(--surface2)', borderRadius: 8 }}>
            <strong style={{ color: '#d32f2f' }}>Backup-Codes — sicher aufbewahren!</strong>
            <p style={{ fontSize: 12, margin: '6px 0' }}>Jeder Code ist genau einmal verwendbar (z.B. wenn du dein Phone verlierst).</p>
            <pre style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, background: '#fff', padding: 10, borderRadius: 6 }}>
{backupCodes.join('\n')}
            </pre>
          </div>
        )}
        {error && <div style={errorBox}>{error}</div>}
      </div>
    );
  }

  if (stage === 'enabled') {
    return (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginTop: 0, color: '#2e7d32' }}>
          ✓ 2FA ist aktiv{enabledAt ? ` seit ${new Date(enabledAt).toLocaleDateString('de-DE')}` : ''}.
        </p>
        <p style={{ color: 'var(--text2)' }}>
          Verbleibende Backup-Codes: <strong>{backupCount}</strong>
          {backupCount < 3 && <span style={{ color: '#d32f2f', marginLeft: 6 }}>— bald neu generieren!</span>}
        </p>
        <button onClick={() => setStage('disable')} style={btnSecondary}>2FA deaktivieren</button>
        {backupCodes.length > 0 && (
          <pre style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, background: 'var(--surface2)', padding: 10, borderRadius: 6, marginTop: 12 }}>
{backupCodes.join('\n')}
          </pre>
        )}
      </div>
    );
  }

  // disable stage
  return (
    <div style={{ fontSize: 13 }}>
      <p style={{ marginTop: 0 }}>Bestätige Passwort und aktuellen 2FA-Code, um 2FA zu deaktivieren.</p>
      <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Passwort"
        style={{ padding: '8px 12px', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 14, marginBottom: 8, width: 240, display: 'block' }} />
      <input type="text" inputMode="numeric" maxLength={6} value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
        placeholder="123456"
        style={{ padding: '8px 12px', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 16, letterSpacing: 4, textAlign: 'center', width: 140 }} />
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button onClick={confirmDisable} disabled={busy || code.length !== 6 || !password} style={{ ...btnPrimary, background: '#d32f2f' }}>Deaktivieren</button>
        <button onClick={() => setStage('enabled')} disabled={busy} style={btnSecondary}>Abbrechen</button>
      </div>
      {error && <div style={errorBox}>{error}</div>}
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '7px 14px', background: 'var(--accent)', color: '#fff',
  border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
};
const btnSecondary: React.CSSProperties = {
  padding: '7px 14px', background: 'var(--surface)', color: 'var(--text2)',
  border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13,
};
const errorBox: React.CSSProperties = {
  marginTop: 10, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca',
  color: '#dc2626', borderRadius: 8, fontSize: 13,
};
